import { CSSProperties, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Form, Modal, Select, Space, Spin, Typography, message } from 'antd'
import classNames from 'classnames'
import { assignBracketSlot, BracketResponse, BracketSlot as ApiBracketSlot, getBracket } from 'services/brackets'
import { getTeamsToLeague } from 'services/teams_to_leagues'
import { TeamToLeague } from 'types/leagues'
import { Game } from 'types/games'
import { BracketCell, BracketModel, buildBracketModel, getStageLabel } from 'utils/buildBracketModel'
import styles from './index.module.scss'

const { Text, Title } = Typography

const teamName = (game: Game | null | undefined, side: 'home' | 'away') => {
    if (!game) return 'TBD'
    if (side === 'home') {
        return game.teamHome?.team?.name || (game.teamHomeId ? `#${game.teamHomeId}` : 'TBD')
    }
    return game.teamAway?.team?.name || (game.teamAwayId ? `#${game.teamAwayId}` : 'TBD')
}

const scoreLabel = (game: Game | null | undefined) => {
    if (!game || game.homeScore == null || game.awayScore == null) return '—'
    const pens = game.homePen != null && game.awayPen != null ? ` (${game.homePen}:${game.awayPen})` : ''
    return `${game.homeScore}:${game.awayScore}${pens}`
}

const gameOptionLabel = (game: Game) => {
    const stage = game.stage?.name ?? '?'
    return `#${game.id} [${stage}] ${teamName(game, 'home')} — ${teamName(game, 'away')} ${scoreLabel(game)}`
}

const teamScoreParts = (game: Game | null | undefined, side: 'home' | 'away') => {
    if (!game || game.homeScore == null || game.awayScore == null) {
        return { main: '—' as const, pen: undefined as number | undefined }
    }

    const main = side === 'home' ? game.homeScore : game.awayScore
    const pen = side === 'home' ? game.homePen : game.awayPen

    return {
        main: String(main),
        pen: pen ?? undefined,
    }
}

const getGameWinner = (game: Game | null | undefined): 'home' | 'away' | 'draw' | undefined => {
    if (!game) return undefined

    if (typeof game.homePen === 'number' && typeof game.awayPen === 'number') {
        if (game.homePen > game.awayPen) return 'home'
        if (game.homePen < game.awayPen) return 'away'
        return 'draw'
    }

    if (typeof game.homeScore === 'number' && typeof game.awayScore === 'number') {
        if (game.homeScore > game.awayScore) return 'home'
        if (game.homeScore < game.awayScore) return 'away'
        return 'draw'
    }

    return undefined
}

const cellLabel = (cell: BracketCell) => {
    if (cell.game) {
        return {
            home: teamName(cell.game, 'home'),
            away: teamName(cell.game, 'away'),
            homeScore: teamScoreParts(cell.game, 'home'),
            awayScore: teamScoreParts(cell.game, 'away'),
            winner: getGameWinner(cell.game),
        }
    }
    if (cell.team) {
        return {
            home: cell.team.name,
            away: 'BYE',
            homeScore: { main: '—' as const, pen: undefined },
            awayScore: { main: '', pen: undefined },
            winner: undefined,
        }
    }
    return {
        home: 'TBD',
        away: 'TBD',
        homeScore: { main: '—' as const, pen: undefined },
        awayScore: { main: '—' as const, pen: undefined },
        winner: undefined,
    }
}

const gameMatchesSlotStage = (game: Game, slot: ApiBracketSlot) => {
    const slotStageName = slot.stage?.name
    const gameStageName = game.stage?.name

    if (slotStageName && gameStageName) {
        return slotStageName === gameStageName
    }

    if (slot.stageId && game.stageId) {
        return slot.stageId === game.stageId
    }

    return false
}

const renderTeamScore = (score: { main: string; pen?: number }, isWinner: boolean) => (
    <span className={styles.slotTeamScore}>
        <span className={classNames({ [styles.slotTeamScoreWinner]: isWinner })}>{score.main}</span>
        {score.pen != null ? <span className={styles.slotTeamPen}>({score.pen})</span> : null}
    </span>
)

export const Bracket = memo(() => {
    const { league: leagueIdParam } = useParams()
    const leagueId = Number(leagueIdParam)

    const [data, setData] = useState<BracketResponse | null>(null)
    const [teams, setTeams] = useState<TeamToLeague[]>([])
    const [loading, setLoading] = useState(false)
    const [slotTarget, setSlotTarget] = useState<ApiBracketSlot | null>(null)
    const [selectedGameId, setSelectedGameId] = useState<number | null>(null)
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
    const [quickAssignSlotByGame, setQuickAssignSlotByGame] = useState<Record<number, number>>({})

    const load = useCallback(async () => {
        if (!leagueId) return
        setLoading(true)
        try {
            const [bracket, teamsToLeague] = await Promise.all([getBracket(leagueId), getTeamsToLeague(leagueId)])
            setData(bracket)
            setTeams(teamsToLeague)
        } finally {
            setLoading(false)
        }
    }, [leagueId])

    useEffect(() => {
        void load()
    }, [load])

    const model: BracketModel | null = useMemo(() => {
        if (!data) return null
        return buildBracketModel(data.slots, data.league.bracketSize)
    }, [data])

    const slotById = useMemo(() => {
        const map = new Map<number, ApiBracketSlot>()
        for (const slot of data?.slots ?? []) {
            map.set(slot.id, slot)
        }
        return map
    }, [data])

    const openSlot = useCallback(
        (cell: BracketCell) => {
            const slot = slotById.get(cell.id)
            if (!slot) {
                message.warning('Slot is not available. Set bracket size on the league first.')
                return
            }
            setSlotTarget(slot)
            setSelectedGameId(slot.gameId)
            setSelectedTeamId(slot.teamId)
        },
        [slotById]
    )

    const assignGameOptions = useMemo(() => {
        if (!data || !slotTarget) return []

        const current = slotTarget.gameId != null ? data.slots.find(s => s.id === slotTarget.id)?.game ?? null : null

        const list = data.unassignedGames.filter(game => gameMatchesSlotStage(game, slotTarget))

        if (current && !list.some(game => game.id === current.id)) {
            list.unshift(current)
        }

        return list
    }, [data, slotTarget])

    const onAssignGame = useCallback(async () => {
        if (!slotTarget || selectedGameId == null) return
        setLoading(true)
        try {
            const result = await assignBracketSlot(leagueId, slotTarget.id, { gameId: selectedGameId })
            setData(result)
            setSlotTarget(null)
            message.success('Game assigned to slot')
        } finally {
            setLoading(false)
        }
    }, [leagueId, selectedGameId, slotTarget])

    const onAssignBye = useCallback(async () => {
        if (!slotTarget || selectedTeamId == null) return
        setLoading(true)
        try {
            const result = await assignBracketSlot(leagueId, slotTarget.id, { teamId: selectedTeamId })
            setData(result)
            setSlotTarget(null)
            message.success('Bye team assigned to slot')
        } finally {
            setLoading(false)
        }
    }, [leagueId, selectedTeamId, slotTarget])

    const onClearSlot = useCallback(async () => {
        if (!slotTarget) return
        setLoading(true)
        try {
            const result = await assignBracketSlot(leagueId, slotTarget.id, { gameId: null, teamId: null })
            setData(result)
            setSlotTarget(null)
            message.success('Slot cleared')
        } finally {
            setLoading(false)
        }
    }, [leagueId, slotTarget])

    const emptySlotOptions = useMemo(() => {
        if (!data) return []

        return data.slots
            .filter(slot => slot.gameId == null && slot.teamId == null)
            .slice()
            .sort((a, b) => {
                const stageA = a.stage?.name ?? ''
                const stageB = b.stage?.name ?? ''
                if (stageA !== stageB) return stageA.localeCompare(stageB)
                return a.slotIndex - b.slotIndex
            })
            .map(slot => ({
                value: slot.id,
                label: `#${slot.id} · ${getStageLabel(slot.stage?.name ?? '')} · slot ${slot.slotIndex}`,
                stageName: slot.stage?.name ?? '',
            }))
    }, [data])

    const slotOptionsForGame = useCallback(
        (game: Game) => {
            const stageName = game.stage?.name
            if (!stageName) return emptySlotOptions

            return emptySlotOptions.filter(option => option.stageName === stageName)
        },
        [emptySlotOptions]
    )

    const unassignedGames = useMemo(() => {
        if (!data) return []

        const stagesWithEmptySlots = new Set(
            data.slots
                .filter(slot => slot.gameId == null && slot.teamId == null)
                .map(slot => slot.stage?.name)
                .filter((stageName): stageName is string => Boolean(stageName))
        )

        return data.unassignedGames.filter(game => game.stage?.name && stagesWithEmptySlots.has(game.stage.name))
    }, [data])

    const onQuickAssign = useCallback(
        async (gameId: number) => {
            const slotId = quickAssignSlotByGame[gameId]
            if (slotId == null) {
                message.warning('Select a slot first')
                return
            }

            setLoading(true)
            try {
                const result = await assignBracketSlot(leagueId, slotId, { gameId })
                setData(result)
                setQuickAssignSlotByGame(prev => {
                    const next = { ...prev }
                    delete next[gameId]
                    return next
                })
                message.success(`Game #${gameId} assigned to slot #${slotId}`)
            } finally {
                setLoading(false)
            }
        },
        [leagueId, quickAssignSlotByGame]
    )

    const renderSlotCard = (stageName: string, cell: BracketCell) => {
        const labels = cellLabel(cell)
        return (
            <div key={`${stageName}-${cell.id}-${cell.slotIndex}`} className={styles.slotWrap}>
                <button type='button' className={styles.slot} onClick={() => openSlot(cell)}>
                    <div className={styles.slotMeta}>
                        {getStageLabel(stageName)} · slot {cell.slotIndex}
                        {cell.id > 0 ? ` · #${cell.id}` : ''}
                    </div>
                    <div className={styles.slotTeams}>
                        <div className={styles.slotTeamRow}>
                            <span className={styles.slotTeamName}>{labels.home}</span>
                            {renderTeamScore(labels.homeScore, labels.winner === 'home')}
                        </div>
                        {labels.away ? (
                            <div className={styles.slotTeamRow}>
                                <span className={styles.slotTeamName}>{labels.away}</span>
                                {labels.awayScore.main
                                    ? renderTeamScore(labels.awayScore, labels.winner === 'away')
                                    : null}
                            </div>
                        ) : null}
                    </div>
                </button>
            </div>
        )
    }

    const renderSide = (cells: BracketCell[], stageName: string, sideClassName: string, depth: number) => (
        <div className={sideClassName} style={{ '--round-depth': depth } as CSSProperties}>
            <div className={styles.sideMatches}>{cells.map(cell => renderSlotCard(stageName, cell))}</div>
        </div>
    )

    if (loading && !data) {
        return <Spin />
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        Bracket · {data?.league?.name ?? '…'}
                    </Title>
                    <Text type='secondary'>
                        Кликните по ячейке, чтобы назначить матч или автопроход. Счёт редактируется в{' '}
                        <Link to={`/leagues/${leagueId}/games`}>играх</Link>. Размер сетки задаётся в форме лиги.
                    </Text>
                </div>
                <Button onClick={() => void load()} loading={loading}>
                    Refresh
                </Button>
            </div>

            {model?.isEmpty ? (
                <Text type='secondary'>
                    No bracket slots yet. Edit the league, choose format Stages and a bracket size.
                </Text>
            ) : model ? (
                <div className={styles.gridWrap}>
                    <div
                        className={styles.grid}
                        style={
                            {
                                '--bracket-cols': model.columns,
                                '--bracket-rows': model.rows,
                            } as CSSProperties
                        }
                    >
                        {model.sideRounds.map(round => (
                            <div key={round.stageName} className={styles.round}>
                                {renderSide(round.start, round.stageName, styles.sideColumnStart, round.depth)}
                                {renderSide(round.end, round.stageName, styles.sideColumnEnd, round.depth)}
                            </div>
                        ))}
                        <div className={styles.finals}>
                            <div className={styles.finalsMain}>{renderSlotCard('Final', model.centerMatches[1])}</div>
                            <div className={styles.finalsSecondary}>
                                {renderSlotCard('3d place', model.centerMatches[0])}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            {unassignedGames.length > 0 ? (
                <div className={styles.unassigned}>
                    <Title level={5} style={{ margin: 0 }}>
                        Games without slot
                    </Title>
                    {unassignedGames.map(game => {
                        const options = slotOptionsForGame(game)
                        return (
                            <div key={game.id} className={styles.unassignedRow}>
                                <Text className={styles.unassignedLabel} title={gameOptionLabel(game)}>
                                    {gameOptionLabel(game)}
                                </Text>
                                <Select
                                    className={styles.unassignedSelect}
                                    placeholder='Slot #'
                                    allowClear
                                    showSearch
                                    optionFilterProp='label'
                                    value={quickAssignSlotByGame[game.id]}
                                    onChange={value =>
                                        setQuickAssignSlotByGame(prev => {
                                            const next = { ...prev }
                                            if (value == null) {
                                                delete next[game.id]
                                            } else {
                                                next[game.id] = value
                                            }
                                            return next
                                        })
                                    }
                                    options={options}
                                    disabled={!options.length}
                                />
                                <Button
                                    className={styles.unassignedAssign}
                                    type='primary'
                                    loading={loading}
                                    disabled={quickAssignSlotByGame[game.id] == null}
                                    onClick={() => void onQuickAssign(game.id)}
                                >
                                    Assign
                                </Button>
                            </div>
                        )
                    })}
                </div>
            ) : null}

            <Modal
                title={
                    slotTarget
                        ? `${getStageLabel(slotTarget.stage?.name ?? '')} · slot ${slotTarget.slotIndex}`
                        : 'Slot'
                }
                open={!!slotTarget}
                onCancel={() => setSlotTarget(null)}
                footer={null}
                destroyOnClose
            >
                <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                    <Form layout='vertical' onFinish={onAssignGame}>
                        <Form.Item label='Assign play-off game'>
                            <Select
                                allowClear
                                value={selectedGameId ?? undefined}
                                onChange={value => {
                                    setSelectedGameId(value ?? null)
                                    if (value != null) setSelectedTeamId(null)
                                }}
                                options={assignGameOptions.map(game => ({
                                    value: game.id,
                                    label: gameOptionLabel(game),
                                }))}
                                placeholder='Select game'
                            />
                        </Form.Item>
                        <Button
                            type='primary'
                            htmlType='submit'
                            block
                            disabled={selectedGameId == null}
                            loading={loading}
                        >
                            Save game
                        </Button>
                    </Form>

                    <Form layout='vertical' onFinish={onAssignBye}>
                        <Form.Item label='Or assign bye team (auto-advance, no match)'>
                            <Select
                                allowClear
                                value={selectedTeamId ?? undefined}
                                onChange={value => {
                                    setSelectedTeamId(value ?? null)
                                    if (value != null) setSelectedGameId(null)
                                }}
                                options={teams.map(t => ({
                                    value: t.teamId,
                                    label: t.team.name,
                                }))}
                                placeholder='Select team'
                            />
                        </Form.Item>
                        <Button
                            type='primary'
                            htmlType='submit'
                            block
                            disabled={selectedTeamId == null}
                            loading={loading}
                        >
                            Save bye team
                        </Button>
                    </Form>

                    <Button danger block onClick={onClearSlot} loading={loading}>
                        Clear slot
                    </Button>

                    {slotTarget?.gameId ? <Link to={`/leagues/${leagueId}/games`}>Edit match in Games →</Link> : null}
                </Space>
            </Modal>
        </div>
    )
})
